import { t } from '@lingui/core/macro';
import { claimVoipMasterIfVacant, IPublicSession, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { myCameraStream } from 'state/video';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { sendApiRequest } from 'utils/network';
import { RTCClient } from 'webrtc';
import { EStreamSessionEvents, EWebsocketClientEvents } from 'webrtc/events';
import { SignalerClient } from 'webrtc/signal';
import type { TRTCConnection } from 'webrtc/types';
import { Unsubscribe } from 'utils/events';
import { isOwnDealerPeer } from 'utils/ui/voipRemotes';
import { State } from 'utils/state';
import { applyRemoteSession } from 'state/session/applyRemote';
import { getDirtyKeys } from 'state/session/dirtyKeys';
import { repairPublicSession } from 'state/session/readRepair';
import { isSyncDegraded, setSyncDegraded } from 'state/session/syncStatus';
import { recordSocketGaveUp, recordSocketReconnectAttempt } from 'state/session/syncTelemetry';
import { voipStatsSampler } from './voipStats';
import { isDebugMode } from './debug';
import { isInheritedSession } from 'state/session/inheritedSession';
import { markVoipPeerDeparted, markVoipPeerPresent, notifyVoipPeersChanged } from 'state/session/voipPeerProbe';
import { getVoipTabId } from 'utils/voipTabId';
import { areIceServersExpired, getIceRefreshDelayMs, RETRY_DELAY_MS } from './iceServers';
import type { TIceServerWithTtl } from './iceServers';
import { errorCollection } from 'config/ui/error-collection';
import { EErrorCode } from 'config/errors-codes';

export let broadcastingConnection: SignalerClient;
export let voipRtcConnection: RTCClient;

/**
 * AVS-4302: bumped every time `initWebRtc` (re)builds `voipRtcConnection`. React consumers
 * (`useVoipConnections`) subscribe to it so a rebuild forces them to re-render and re-subscribe
 * to the NEW `RTCClient.store`. Without this, a takeover / step-down rebuilds the connection
 * while the VoipPanel stays mounted (View.tsx keeps `voipStream` true), so the hook keeps its
 * subscription to the OLD (destroyed) store and never sees the new master's loopback — the
 * stepped-down master is stuck on a placeholder. `voipRtcConnection` is a module binding whose
 * reassignment is invisible to React; this State makes the rebuild observable.
 */
export const voipConnectionEpoch = new State<{ value: number }>({ value: 0 });
export let broadcastingConnectionId: string;

// Handler-level reentrancy guard for this SessionUpdated listener: a change listener firing during
// an apply can re-enter this handler synchronously, and the guard keeps that on the short path
// instead of recursing. Distinct from applyRemoteSession's internal runApplyingRemote guard (which
// stops syncToServer dirty-marking the inbound change and self-skipping the next broadcast) —
// different mechanisms, both required (AVS-4330). It no longer drives any cancel of the outbound
// debounced sync; that discard was AVS-4399's root cause.
let isApplyingBroadcastUpdate = false;

// AVS-4330 degraded-mode fallback. When the broadcasting socket gives up reconnecting, the client
// can no longer RECEIVE broadcasts, so it slow-polls the session record to self-heal missed
// (persisted) changes. Owned here — not in initWebRtc — because initSessionBroadcasting also runs
// for voip-disabled / customer sessions, which initWebRtc early-returns out of.
export const SYNC_DEGRADED_POLL_INTERVAL_MS = 30_000;
// Grace before a never-settling broadcast connect falls back to the degraded poll (AVS-4331).
export const BROADCAST_CONNECT_GRACE_MS = 5_000;
let syncPollInterval: ReturnType<typeof setInterval> | null = null;
// Module-scope so a superseding initSessionBroadcasting (new session id) can cancel the
// previous attempt's pending fallback — an orphaned timer would mark the NEW session (AVS-4331).
let broadcastingGraceTimer: ReturnType<typeof setTimeout> | null = null;
let onlineListenerRegistered = false;

function stopSyncDegradedPoll() {
  if (syncPollInterval !== null) {
    clearInterval(syncPollInterval);
    syncPollInterval = null;
  }
  setSyncDegraded(false);
}

function startSyncDegradedPoll() {
  setSyncDegraded(true);
  if (syncPollInterval !== null) {
    return; // idempotent — a second give-up must not start a second timer
  }
  // Jitter so a fleet that all give up at once (a signaling outage) doesn't GET in lockstep.
  const interval = SYNC_DEGRADED_POLL_INTERVAL_MS + Math.floor(Math.random() * 10_000);
  syncPollInterval = setInterval(() => {
    // Self-terminate once the session is gone/ended — covers customers, who never emit
    // 'session:end', so the poll can't outlive the session.
    if (!userSession().get('id') || userSession().get('endedAt')) {
      stopSyncDegradedPoll();
      return;
    }
    void repairPublicSession();
  }, interval);
}

// Connectivity restored: heal immediately, and if we had given up entirely, rebuild the dead
// socket (initSessionBroadcasting makes a fresh SignalerClient, since the dead one fails isReady()).
function handleBroadcastingOnline() {
  void repairPublicSession();
  if (isSyncDegraded()) {
    initSessionBroadcasting();
  }
}

// AVS-4302: the rtc-level EventEmitter handlers registered in initWebRtc
// (`voip:connected`, `peer:disconnected`, `peer:connected` on the RTCClient, and
// `session:end` on the persistent userSession() singleton) are re-registered on every
// initWebRtc. Without removing the previous ones they stack across init→destroy→init
// cycles. `session:end` is the worst offender: it lives on the userSession() singleton,
// which survives RTCClient.instance nulling, so it accumulates across reconnect cycles.
// We collect each registration's unsubscribe here and clear them before re-registering.
let voipHandlerUnsubscribes: Unsubscribe[] = [];

function cleanupVoipHandlers() {
  voipHandlerUnsubscribes.forEach((unsubscribe) => {
    if (typeof unsubscribe === 'function') unsubscribe();
  });
  voipHandlerUnsubscribes = [];
}

async function getIceServers(background = false) {
  // if ICE_SERVERS_ORIGIN is not set, it will go to the default value which is fine for our deployed instances
  // AVS-4391: the background refresh uses a DISTINCT endpoint — sendApiRequest aborts identical
  // in-flight method:url pairs, and the refresh must never cancel a concurrent init's fetch —
  // and owns its error handling (a failed refresh retries silently instead of toasting mid-call).
  const endpoint = background ? '/ice-servers?background=1' : '/ice-servers';
  return sendApiRequest<TIceServerWithTtl[]>(endpoint, {
    origin: process.env.ICE_SERVERS_ORIGIN,
    ...(background && { suppressToast: true }),
  });
}

// AVS-4391: KVS TURN credentials expire (default Ttl 300s) but were fetched once per call and
// cached in the RTCClient singleton — every pc created past the TTL (a late joiner, a rejoin,
// an AVS-4311 transport rebuild) gathered zero relay candidates, silently, and could never
// connect on relay-requiring paths. One module-level timer per tab refetches at ~75% of the
// TTL and pushes the fresh list into the live client; cleared before re-arming so repeated
// init→destroy→init cycles cannot stack timers (same rule as the handler cleanup above).
let iceServersRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function stopIceServersRefresh() {
  if (iceServersRefreshTimer) {
    clearTimeout(iceServersRefreshTimer);
    iceServersRefreshTimer = null;
  }
}

// AVS-4418: the delay and the expiry check read `client.iceServerList` — the sanitised list the
// client actually holds — never the raw body that was fetched. The client drops entries the browser
// would refuse, and a ttl on a dropped entry must not stretch the refresh past the surviving
// relays' lifetime (that would reopen AVS-4391 silently).
function scheduleIceServersRefresh(
  client: RTCClient,
  fetchedAtMs: number,
  delayMs = getIceRefreshDelayMs(client.iceServerList, fetchedAtMs, Date.now())
) {
  stopIceServersRefresh();
  // Pin the client and its session so a refresh landing after a teardown or takeover rebuild is
  // dropped: destroy() clears sessionId (customers never emit session:end, so the cleared
  // sessionId is the load-bearing stop condition), and a rebuilt connection is a new object.
  const pinnedSessionId = client.sessionId;
  iceServersRefreshTimer = setTimeout(async () => {
    if (voipRtcConnection !== client || !pinnedSessionId || client.sessionId !== pinnedSessionId) return;

    // Stamped BEFORE dispatch: a slow response must not make the new credentials look fresher
    // than they are and eat into the safety margin before the next refresh.
    const refreshStartedAt = Date.now();
    let fresh: TIceServerWithTtl[] | undefined;
    try {
      fresh = await getIceServers(true);
    } catch (error) {
      // suppressToast means sendApiRequest stays silent, so this is the only record of a failed
      // attempt. Without it the first log line appears only once the credentials are already
      // dead, which is exactly the window a field diagnosis needs to see.
      logger.warn('[VOIP] ICE server credential refresh failed, will retry', error);
      fresh = undefined;
    }

    // Re-check after the await — the call can end while the fetch is in flight.
    if (voipRtcConnection !== client || client.sessionId !== pinnedSessionId) return;

    if (!fresh) {
      if (areIceServersExpired(client.iceServerList, fetchedAtMs, Date.now())) {
        console.error(
          '[VOIP] ICE server credentials are past their TTL and the refresh failed — ' +
            'peer connections created now will gather no relay candidates until a refetch succeeds'
        );
      }
      scheduleIceServersRefresh(client, fetchedAtMs, RETRY_DELAY_MS);
      return;
    }

    client.updateIceServers(fresh, refreshStartedAt);
    scheduleIceServersRefresh(client, refreshStartedAt);
  }, delayMs);
}

export function initSessionBroadcasting() {
  // Sometimes session ID is not yet available. If so, exit early and retry when session ID becomes available
  if (!userSession().get('id')) {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let unsubscribed = false;

    const unsub = userSession().on('change', (changedKeys, changedValues, oldValues, newValues, session) => {
      if (changedKeys.includes('id') && session.id && !unsubscribed) {
        if (timeoutId) clearTimeout(timeoutId);
        unsubscribed = true;
        unsub();
        initSessionBroadcasting();
      }
    });

    // Failsafe: If session ID is not set within 10 seconds, log error and unsubscribe
    timeoutId = setTimeout(() => {
      if (!unsubscribed) {
        unsubscribed = true;
        unsub();
        console.error('[Session Broadcast] Timeout waiting for session ID - broadcasting not initialized');
      }
    }, 10000);

    logger.log('[Session Broadcast] Waiting for session ID to initialize broadcasting');
    return;
  }

  // Register the online-recovery listener once per tab (initSessionBroadcasting is re-entrant).
  if (!onlineListenerRegistered && typeof window !== 'undefined') {
    window.addEventListener('online', handleBroadcastingOnline);
    onlineListenerRegistered = true;
  }

  const sessionId = userSession().get('id');
  // AVS-4425: `clientId` is this tab's stable id (`getVoipTabId()`, sessionStorage-backed, so it
  // survives a reload). It is the ONLY correlatable identity a customer has on the wire, and it
  // lets `$connect` supersede this tab's previous socket instead of leaving a ghost peer behind.
  // Stable for the tab's lifetime, so the URL-changed comparison below is unaffected.
  const broadcastingApiUrl = `${process.env.SIGNALING_ORIGIN}?sessionId=${sessionId}&topicId=session&clientId=${encodeURIComponent(getVoipTabId())}`;

  if (broadcastingConnection && broadcastingConnection.isReady()) {
    if (broadcastingConnection.wsApiUrl !== broadcastingApiUrl) {
      // URL changed, disconnect and reconnect
      console.warn('[Session Broadcast] Broadcasting connection URL changed, disconnecting');
      broadcastingConnection.disconnect();
    } else {
      // Same URL, no need to reconnect. Relies on reset always minting a new session id
      // (schema default) — a same-id reset would return here with the readiness flag cleared
      // and nothing left to re-mark it.
      return;
    }
  } else if (broadcastingConnection) {
    // A not-yet-ready predecessor (pending or mid-reconnect) is being superseded: stop its
    // reconnect scheduling so its callbacks can never act for the new session (AVS-4331).
    broadcastingConnection.disconnect();
  }
  if (broadcastingGraceTimer !== null) {
    clearTimeout(broadcastingGraceTimer);
    broadcastingGraceTimer = null;
  }

  broadcastingConnection = new SignalerClient(broadcastingApiUrl);
  // Ownership guard: every async callback below belongs to THIS client. A superseding
  // initSessionBroadcasting replaces the module binding, and a stale callback must never
  // mark readiness or touch the poll for a session it no longer represents (AVS-4331).
  const client = broadcastingConnection;
  // Truncated id + origin only: full session ids are mutation-capable and host pages
  // commonly ship console.error breadcrumbs to third-party telemetry.
  const logContext = {
    sessionId: `${String(sessionId).slice(0, 8)}…`,
    url: broadcastingApiUrl.split('?')[0],
  };

  // AVS-4331: a socket that opens and drops pre-handshake in a loop leaves connect() forever
  // pending — neither resolved nor rejected. Treat that like a rejected connect after a short
  // grace window: engage the degraded poll and mark, while the reconnect loop keeps trying.
  // console.error (not logger) is deliberate — the module's hard-failure pattern, visible in prod.
  broadcastingGraceTimer = setTimeout(() => {
    broadcastingGraceTimer = null;
    if (broadcastingConnection !== client) return;
    console.error('[Session Broadcast] WebSocket not established within grace window — degraded sync poll', logContext);
    startSyncDegradedPoll();
    userSession().markRequirementReady('broadcastingEstablished');
  }, BROADCAST_CONNECT_GRACE_MS);
  const clearGraceTimer = () => {
    if (broadcastingGraceTimer !== null) {
      clearTimeout(broadcastingGraceTimer);
      broadcastingGraceTimer = null;
    }
  };

  client
    .connect()
    .then((connection) => {
      if (broadcastingConnection !== client) return;
      clearGraceTimer();
      broadcastingConnectionId = connection.connectionId;
      // A fresh successful connect (including an `online`-triggered rebuild after give-up) means the
      // socket is healthy again — leave degraded mode and stop the slow-poll (AVS-4330). This is the
      // clear path that WsClientReconnected cannot provide after a give-up (it only fires from the
      // auto-reconnect loop, which has already stopped).
      stopSyncDegradedPoll();
      // AVS-4331: only a RESOLVED connect proves broadcasting is live — marking synchronously (the
      // old behaviour) advanced the customer waiting room on a socket that never connected. A reset
      // clears the flag; every re-establishment path (new session id ⇒ new URL ⇒ rebuild) re-marks
      // here. Idempotent flip-once under markRequirementReady.
      userSession().markRequirementReady('broadcastingEstablished');
      logger.info('[Session Broadcast] WebSocket connected', { connectionId: connection.connectionId });
    })
    .catch((error: unknown) => {
      if (broadcastingConnection !== client) return;
      clearGraceTimer();
      // AVS-4331: a REJECTED connect engages the AVS-4330 degraded slow-poll immediately — session
      // syncing is then genuinely operational (poll + PUTs), so the flag is truthfully marked and
      // streaming proceeds degraded instead of blocking for the ~3min reconnect loop. The reconnect
      // loop keeps running in parallel; WsClientReconnected below stops the poll and read-repairs.
      console.error('[Session Broadcast] WebSocket connect failed — falling back to degraded sync poll', {
        ...logContext,
        error,
      });
      startSyncDegradedPoll();
      userSession().markRequirementReady('broadcastingEstablished');
    });

  // Keep the echo-suppression key fresh across reconnects. The server drops broadcasts
  // whose `emitter` matches the sender's connection id; after a silent reconnect the
  // SignalerClient mints a NEW connection id, so without this the stale id no longer
  // matches and the client receives — and re-applies — its own writes. Mirrors the media
  // RTCClient's WsClientReconnected handling (webrtc/index.ts).
  client.on(EWebsocketClientEvents.WsClientReconnected, (data) => {
    if (broadcastingConnection !== client) return;
    const newConnectionId = (data as { connectionId?: string } | undefined)?.connectionId;
    if (newConnectionId) {
      broadcastingConnectionId = newConnectionId;
      logger.info('[Session Broadcast] Reconnected — refreshed broadcasting connection id', {
        connectionId: newConnectionId,
      });
    }
    // Leave degraded mode and read-repair: a reconnect means we may have missed broadcasts while
    // the socket was down (AVS-4330). Dirty-key protection keeps any unflushed local edit.
    stopSyncDegradedPoll();
    // AVS-4331: a rejected initial connect() can never resolve; the reconnect event is the recovery path.
    userSession().markRequirementReady('broadcastingEstablished');
    void repairPublicSession();
  });

  // AVS-4332: socket-health telemetry for the SESSION-BROADCAST socket. Recorded above the
  // ownership guard on purpose — the counters are tab-lifetime socket-health numbers, not session
  // state, so a superseded client's scheduled reconnect is still a real broadcast-socket retry.
  // The guard exists to stop a stale callback acting for a session it no longer represents.
  client.on(EWebsocketClientEvents.WsClientReconnecting, () => {
    recordSocketReconnectAttempt('broadcast');
  });

  // Auto-reconnect exhausted its attempts: fall back to a slow-poll read-repair so persisted state
  // stays fresh even though real-time broadcasts are dead (AVS-4330).
  client.on(EWebsocketClientEvents.WsClientGaveUp, () => {
    recordSocketGaveUp('broadcast');
    if (broadcastingConnection !== client) return;
    startSyncDegradedPoll();
  });

  logger.log('[Session Broadcast] Broadcasting connection initialized');

  broadcastingConnection.on(EStreamSessionEvents.SessionUpdated, (data) => {
    // Log the shape, never the payload: a session update carries `partnerToken` (and dealer
    // identity), and this line runs at `log` level — which E2E runs and field debugging turn
    // on, putting the token into console output and uploaded CI traces.
    logger.log('[Session Broadcast] Session update received', {
      keys: data && typeof data === 'object' ? Object.keys(data) : typeof data,
    });

    if (!data || typeof data !== 'object') {
      return;
    }

    const aveSessionId = (data as { aveSessionId?: string }).aveSessionId;
    logger.info('[Session Broadcast] aveSessionId from broadcast update', aveSessionId);

    // Guard against a stale server echo overwriting an aveSessionId set locally
    // by the integrator (e.g. LCC) in connectionCallback. The echo may carry
    // aveSessionId:'' if share()'s updatePublicSessionNow() fired before the
    // integrator's write reached the server — AVS-4267.
    const sessionData = data as Partial<IPublicSession>;
    let safeData: Partial<IPublicSession> = sessionData;
    if (aveSessionId === '' && userSession().get('aveSessionId')) {
      const copy: Partial<IPublicSession> = { ...sessionData };
      delete copy.aveSessionId;
      safeData = copy;
    }

    // Skip re-entering the main path if already processing a broadcast, to prevent infinite loops
    if (isApplyingBroadcastUpdate) {
      applyRemoteSession(safeData, { dirtyKeys: getDirtyKeys() });
      return;
    }

    // An inbound broadcast must NOT discard the pending outbound debounced write (AVS-4399).
    //
    // This used to call updatePublicSession.cancel() before and after applying, justified as
    // "the server broadcast is authoritative". It is not authoritative for keys the broadcast
    // does not carry, and for the keys it does carry the local edit is already protected —
    // applyRemoteSession skips dirty keys and gates by version. So the cancel was redundant
    // where it was safe, and destructive everywhere else: markDirty runs before the PUT is
    // scheduled and clearDirty only runs on a confirmed response, so a cancelled window left
    // its keys dirty with no driver able to flush them. The dealer's carImages then never
    // reached the server for the remainder of the session.
    //
    // The 3D→2D reversion this once guarded against is prevented by atomic aveSessionId clearing
    // in experience.ts plus the immediate (debounce-bypassing) sync in syncToServer.ts, and the
    // clobber guard now names keys to omit rather than freezing a snapshot that could go stale
    // inside the window (see updatePublicSessionNow's `omitKeys`).
    isApplyingBroadcastUpdate = true;
    try {
      // Routed through the single remote-apply chokepoint: it drops stale/reordered
      // payloads by version and applies only the keys the server actually sent (AVS-4330).
      applyRemoteSession(safeData, { dirtyKeys: getDirtyKeys() });
    } finally {
      isApplyingBroadcastUpdate = false;
    }
  });
}

/**
 * AVS-4461: is THIS tab's current VOIP connection working for `sessionId`? True only when the
 * live binding serves that session AND its `connect()` has resolved: `initWebRtc` installs the
 * client and its sessionId before awaiting connect(), so a matching sessionId alone is not enough.
 * Gates both automatic standby claims (the slot trigger and the `initWebRtc` tail).
 */
export function hasReadyVoipConnection(sessionId: string | undefined): boolean {
  return !!sessionId && voipRtcConnection?.sessionId === sessionId && voipRtcConnection.connected;
}

/** A camera is only "working" when the stream carries a video track that has not ended. */
function hasLiveVideoTrack(stream: MediaStream | null): boolean {
  return Boolean(stream?.getVideoTracks().some((track) => track.readyState === 'live'));
}

/**
 * Should this tab show the explicit media-recovery control? True only for the two states with
 * no working affordance: no local stream at all (the camera/mic toggles are dead —
 * `RTCClient.toggleMedia` early-returns without `me.stream`), or an audio-only stream whose
 * camera toggle is hidden. Audio-only WITH a visible camera toggle returns false: that toggle
 * re-acquires the missing track itself, and one working affordance beats two competing ones.
 * Mic-only failures are deliberately out of scope — the mic toggle re-acquires its own track
 * the same way.
 */
export function needsVoipMediaRecovery(): boolean {
  // AVS-4461: a pending owner's only camera path is "Take over" — never offer a second one.
  if (userSession().isVoipClaimPendingMedia) {
    return false;
  }

  const camera = myCameraStream();

  // No getUserMedia in this browser at all: a retry could never succeed, and the
  // "not supported" warning has already been shown.
  if (!camera.get('mediaAllowed')) {
    return false;
  }

  if (!(camera.stream instanceof MediaStream)) {
    return true;
  }

  if (hasLiveVideoTrack(camera.stream)) {
    return false;
  }

  return !camera.get('isVideoAllowed');
}

/**
 * Re-acquire local media for a dealer who started the call without a camera and hand it to the
 * live VOIP connection. Resolves true once a live video track is publishing. With no stream it
 * runs a full `init({video:true, audio:true})` — explicit config so no persisted preference can
 * suppress the camera — and registers whatever comes back (an audio-only grab still makes the
 * dealer heard). With an audio-only stream it awaits `reinitializeTrack('video')` directly, so
 * the control can report a still-broken camera (`enableTrack` swallows the failure).
 */
export async function retryVoipMedia(): Promise<boolean> {
  // AVS-4461: a pending owner starts camera and mic only through "Take over", which also moves the
  // claim out of pending. Guarded here, not in the component, so no caller can skip that step.
  if (userSession().isVoipClaimPendingMedia) {
    return false;
  }
  // Coalesce concurrent retries: two clicks would both await the same getUserMedia and then
  // both register — and `shareMyStream()` overwrites `RTCClient.listeners` without
  // unsubscribing, leaking a listener pair per extra click.
  if (retryVoipMediaInFlight) {
    return retryVoipMediaInFlight;
  }
  retryVoipMediaInFlight = runVoipMediaRetry().finally(() => {
    retryVoipMediaInFlight = null;
  });
  return retryVoipMediaInFlight;
}

let retryVoipMediaInFlight: Promise<boolean> | null = null;

async function runVoipMediaRetry(): Promise<boolean> {
  const camera = myCameraStream();

  if (hasLiveVideoTrack(camera.stream)) {
    return true;
  }

  // Acquisition waits on a permission prompt the user can leave open indefinitely, so the call
  // it belongs to may be gone by the time it resolves. Pin the identity now and re-check after.
  const connectionAtStart = voipRtcConnection;
  const sessionAtStart = connectionAtStart?.sessionId;

  // Which branch ran decides whether the result needs registering (see below).
  const acquiredWholeStream = !camera.stream;

  if (acquiredWholeStream) {
    await camera.init({ video: true, audio: true }).catch((error: unknown) => {
      logger.log('[VOIP] Media recovery failed to acquire a stream', error);
    });
  } else {
    await camera.reinitializeTrack('video').catch((error: unknown) => {
      logger.log('[VOIP] Media recovery failed to re-acquire the camera', error);
    });
  }

  // The call ended (or moved on) while the prompt was open: nothing owns a stream acquired
  // after teardown, so publishing would hit a dead client and returning would leave the
  // camera light on.
  if (!voipRtcConnection || voipRtcConnection.sessionId !== sessionAtStart) {
    logger.log('[VOIP] Media recovery completed after the call ended — releasing the camera');
    camera.stopStream();
    return false;
  }

  // Same session, new client: a takeover rebuilt the connection mid-prompt. A rebuilt master
  // acquires and registers its own media, so the stream is left to it — but a standby never
  // grabs media, so a late stream would keep the camera live with no owner.
  if (voipRtcConnection !== connectionAtStart && userSession().isVoipStandby) {
    logger.log('[VOIP] Media recovery completed after a step-down — releasing the camera');
    camera.stopStream();
    return false;
  }

  // Only the whole-stream branch registers: a re-acquired track travels via `track:added`, and
  // a rebuilt client (takeover) runs its own registration — publishing into either would stack
  // listeners.
  if (acquiredWholeStream && camera.stream && voipRtcConnection === connectionAtStart) {
    voipRtcConnection.registerOutgoingStream();
  }

  if (!hasLiveVideoTrack(camera.stream)) {
    return false;
  }

  // Re-read permissions (nothing else re-polls after the initial grab, so `isVideoAllowed`
  // would stay false and keep the toggle hidden) and record the camera as ON.
  camera.checkPermissions();
  camera.markVideoRecovered();

  return true;
}

export async function initWebRtc(
  sessionId = userSession().get('id'),
  callback?: () => void
): Promise<RTCClient | null> {
  // only remote sessions can initialize webrtc and voip should be allowed
  if (!userSession().voipEnabled) {
    // voip not allowed
    return null;
  }

  // AVS-4286: a dealer tab that inherited this session from another browsing context
  // must not publish its own dealer VOIP stream. When 3DE standalone opens the DCC
  // configurator it passes the session via the `e3d-sid` URL param; that DCC tab is
  // cross-origin, so the two tabs cannot coordinate client-side — instead each tab
  // self-identifies from its own inherited-session record. Without this guard both tabs
  // publish and the customer sees the dealer twice. The originating standalone tab
  // (no inherited record) keeps the single dealer stream; customers are unaffected.
  //
  // AVS-4302: under the single-stream election this narrow suppression is superseded.
  // An inherited DCC tab must be allowed to CONNECT (as a receive-only standby) so the
  // customer-side dedup and manual takeover work — returning null here would deny it a
  // connection entirely. So the guard only applies when the election is OFF, in which
  // case behaviour is exactly as today (skew-safe, minimal regression).
  if (!userSession().isVoipSingleStreamEnabled) {
    if (user.type === 'dealer' && !!sessionId && isInheritedSession(sessionId)) {
      return null;
    }
  }

  // already connected, call callback immediately if provided
  if (voipRtcConnection?.sessionId === sessionId) {
    if (callback) {
      callback();
    }
    return voipRtcConnection;
  }

  // if presentation if completed but we try to init rtc, then something is wrong.
  // proceeding is not allowed and also make sure that the previous connection is properly destroyed
  if (userSession().presentationCompleted) {
    voipRtcConnection?.destroy();
    return null;
  }

  // AVS-4302: a non-master standby (another tab of the SAME dealer already owns the
  // voipMasters[myPseudonym] slot) must never grab the camera. Skipping the whole block means it
  // never calls `myCameraStream().init()` AND the receive-only toast below never fires
  // for an INTENTIONAL standby (GATE W6) — a standby is receive-only by design, not by
  // permission denial. `isVoipStandby` is false when the flag is off and false for a
  // different dealer / a vacant slot, so a flag-off tab and a vacant-slot dealer still
  // grab the camera exactly as today.
  // AVS-4461: a master claim pending the "Take over" click skips the camera the same way. The
  // decision comes from those two computeds only — never from `.stream` (a camera-denied
  // publisher still built WITH own media) — and is stamped on the client below.
  const withoutOwnMedia = userSession().isVoipWithoutOwnMedia;
  // Try to initialize camera stream, but continue establishing RTC connection even if it fails
  // This allows receive-only mode for users who deny camera/mic permissions
  // AVS-4418: remember whether THIS call grabbed the camera. If the ICE fetch below fails there
  // is no client to own that stream, but a camera the caller already held is not ours to stop.
  let cameraAcquiredHere = false;

  if (!withoutOwnMedia && !myCameraStream().stream) {
    const initPromise = new Promise<void>((resolve) => {
      let unsubscribeStarted: (() => void) | null = null;
      let unsubscribeError: (() => void) | null = null;

      const onStreamStarted = () => {
        cleanup();
        resolve();
      };

      const onStreamError = () => {
        cleanup();
        // Don't abort - continue in receive-only mode
        logger.log('[VOIP] Camera/mic denied - continuing in receive-only mode');

        // Show warning toast to inform user about receive-only mode
        showToast({
          title: t`Camera and microphone unavailable`,
          message: t`Your camera and/or microphone are not allowed or not available. You will only be able to see the other party's video stream. To enable your camera and microphone, update your browser permissions and reload the page.`,
          type: 'warn',
          notificationId: 'WARN_VOIP_RECEIVE_ONLY',
        });

        resolve();
      };

      const cleanup = () => {
        if (unsubscribeStarted) unsubscribeStarted();
        if (unsubscribeError) unsubscribeError();
      };

      unsubscribeStarted = myCameraStream().once('stream:started', onStreamStarted);
      unsubscribeError = myCameraStream().once('stream:error', onStreamError);

      myCameraStream().init();
    });

    // Wait for stream initialization attempt, then continue regardless of result
    await initPromise;
    // Set even when the grab failed (receive-only): `stopStream()` no-ops without a stream, and
    // the alternative — inspecting `.stream` here — would miss a stream that arrived late.
    cameraAcquiredHere = true;
  }

  // Stamped BEFORE the fetch so a slow response cannot make the credentials look fresher than
  // they are — the refresh margin is carved out of the real remaining lifetime (AVS-4391).
  const iceServersFetchedAt = Date.now();
  // AVS-4418: `sendApiRequest` THROWS an ApiError on a non-2xx — it does not resolve null — and
  // the ice-servers lambda now answers 500 when KVS hands back no usable TURN relay. Without this
  // catch the rejection escapes initWebRtc: the `if (!iceServers)` branch below never runs, and
  // neither View.tsx caller attaches a handler (`initWebRtc(...)` / `void initWebRtc(...)`), so it
  // surfaces as an unhandled rejection while the camera grabbed above keeps capturing with no
  // client to own it. Funnel the failure into the existing no-ICE-servers path instead.
  let iceServers: Awaited<ReturnType<typeof getIceServers>>;
  let fetchRejected = false;
  try {
    iceServers = await getIceServers();
  } catch (error) {
    fetchRejected = true;
    logger.error('[VOIP] ICE server fetch failed — VOIP unavailable for this session', error);
  }
  if (!iceServers) {
    console.error(`No ICE servers found, RTC won't work`);
    // Release ONLY when the fetch genuinely REJECTED. A resolved-but-empty result also means
    // `sendApiRequest` aborted OUR request because a NEWER initWebRtc started against the same
    // method:url pair — a duplicate abort RESOLVES to undefined, it does not throw
    // (utils/network/index.ts, the `'Duplicate request'` branch). DCC deliberately re-fires
    // initWebRtc on an element remount (containers/Stream/View.tsx), and that newer call skips
    // its own camera grab because ours is already live — so stopping the camera here would
    // strand the call it is building with no video and no path to re-acquire.
    if (cameraAcquiredHere && fetchRejected) {
      // Release through the manager so it nulls its own ref (AVS-4363 / camera-lifecycle canon):
      // a raw `track.stop()` leaves a dead stream object behind, and the `!myCameraStream().stream`
      // re-grab guard above would then read it as still live and never re-acquire.
      logger.log('[VOIP] Releasing the camera grabbed for a session that has no ICE servers');
      myCameraStream().stopStream();
    }
    return null;
  }
  // A dealer carries a stable per-dealer identity (userPseudonym) so peers can
  // recognise the same human across tabs (AVS-4302). A customer has no dealer
  // identity, so the param is only appended for dealers when a pseudonym exists.
  const dealerPseudonymParam =
    user.type === 'dealer' && user.userPseudonym ? `&dealerPseudonym=${encodeURIComponent(user.userPseudonym)}` : '';
  // AVS-4425: the same stable per-tab id the master election digests (AVS-4454: voipMasters holds
  // only `getVoipSlotId`, so this raw selector travels only on this tab's own signaling socket URL).
  // A dealer already carries `dealerPseudonym`, but that is per-HUMAN, not per-tab; `clientId` is
  // what lets the server retire this exact tab's previous socket, and it is the only correlatable
  // identity a customer has at all.
  const clientIdParam = `&clientId=${encodeURIComponent(getVoipTabId())}`;
  voipRtcConnection = RTCClient.getInstance(
    iceServers,
    `${process.env.SIGNALING_ORIGIN}?sessionId=${sessionId}&topicId=user-stream&userType=${user.type}${dealerPseudonymParam}${clientIdParam}`
  );
  voipRtcConnection.sessionId = sessionId;
  // AVS-4461: after EVERY getInstance — the singleton is reused on an unchanged endpoint.
  voipRtcConnection.withoutOwnMedia = withoutOwnMedia;
  // AVS-4302: announce the (re)built connection so mounted React consumers re-subscribe to the
  // NEW store (a takeover/step-down rebuild happens under a still-mounted VoipPanel — see
  // voipConnectionEpoch). Bump AFTER the reassignment so consumers read the fresh binding.
  voipConnectionEpoch.setValues({ value: voipConnectionEpoch.get('value') + 1 });

  // AVS-4302: drop any handlers left over from a previous initWebRtc before
  // re-registering, so a repeated init→destroy→init cycle does not stack duplicate
  // voip:connected / peer:* / session:end listeners.
  cleanupVoipHandlers();

  if (callback) {
    voipHandlerUnsubscribes.push(voipRtcConnection.on('voip:connected', callback));
  }

  // Pinned before the await, re-checked after it: a destroy or rebuild while connect() is in flight
  // must not let this call claim for a client that is no longer the live one.
  const connection = voipRtcConnection;
  await connection.connect();

  // AVS-4461: re-check vacancy now that the connection works. A release can land while this build
  // awaited ICE credentials: there was no live connection for the slot trigger to claim with, and
  // with zero peers the publish gate never runs. Only a build without own media claims here, and
  // its claim is pending (the camera waits for "Take over"); a media build keeps the gate-only claim.
  if (
    connection.withoutOwnMedia &&
    connection === voipRtcConnection &&
    hasReadyVoipConnection(sessionId) &&
    sessionId === userSession().get('id')
  ) {
    claimVoipMasterIfVacant();
  }
  // AVS-4485: the connection is ready now, so a host claim that waited on it can run.
  if (connection === voipRtcConnection && hasReadyVoipConnection(sessionId)) {
    notifyVoipPeersChanged();
  }

  // AVS-4391: arm the TTL refresh only after a successful connect — arming earlier would leave
  // an orphan timer refreshing credentials for a client that never joined (connect() throws,
  // the session:end stop below is never registered, and the guards all still pass).
  scheduleIceServersRefresh(voipRtcConnection, iceServersFetchedAt);

  // AVS-4419: the prod-safe getStats() sampler behind the session's voipQuality counters. Armed
  // only after a successful connect, for the same reason as the refresh above, and re-armed on
  // every peer:connected (below) so a parked sampler wakes for a late joiner. It re-resolves the
  // live client per tick, so a takeover's rebuilt client needs no re-wiring here.
  voipStatsSampler.arm();

  voipHandlerUnsubscribes.push(
    voipRtcConnection.on(
      'peer:disconnected',
      (_connectionId: string, _remoteConnections: Record<string, TRTCConnection>, meta?: TRTCConnection['meta']) => {
        // AVS-4485: a hand-off bystander re-evaluates when the named successor leaves the call.
        notifyVoipPeersChanged();
        // AVS-4302: don't announce this dealer's own tab leaving (e.g. the old master stepping
        // down on a takeover) — that is "me", not a peer. The leaving peer's meta is captured
        // before removal (see ConnectionRemoved) since it is gone from remoteConnections here.
        if (isOwnDealerPeer({ meta }, user.userPseudonym, user.isDealer)) return;
        showToast({
          title: t`Your peer disconnected from the VOIP session`,
          type: 'warn',
          notificationId: 'WARN_VOIP_PEER_DISCONNECTED',
        });
      }
    )
  );

  // AVS-4485: record a departure only from a real row removal (every tracked ConnectionRemoved), never
  // from absence or a failed transport.
  voipHandlerUnsubscribes.push(
    voipRtcConnection.on('peer:removed', (_connectionId: string, meta?: TRTCConnection['meta']) => {
      const departedSlot = meta?.clientIdHash;
      if (typeof departedSlot === 'string' && departedSlot) markVoipPeerDeparted(departedSlot);
      notifyVoipPeersChanged();
    })
  );

  voipHandlerUnsubscribes.push(
    // This browser will not build peer connections at all — a Permissions-Policy refusal, an
    // extension, or an enterprise policy. Not "your peer disconnected": nobody ever connected, and
    // nobody will, so the message has to name the browser rather than the call. Session
    // `312d2e01-…` (2026-09-05) was this, and the customer was shown nothing whatsoever while the
    // dealer waited out an eight-second convergence timeout.
    voipRtcConnection.on('peer:unavailable', () => {
      const details = errorCollection[EErrorCode.WEBRTC_BLOCKED];

      showToast({
        title: details.title(),
        message: details.message(),
        type: 'error',
        notificationId: details.notificationId,
      });
    })
  );

  voipHandlerUnsubscribes.push(
    voipRtcConnection.on(
      'peer:connected',
      (connectionId: string, remoteConnections: Record<string, TRTCConnection>) => {
        // AVS-4419: a peer connection now exists, so wake a parked stats sampler — BEFORE the
        // own-tab check below, since an own-dealer tab is still a peer in the store. Folded into
        // this listener rather than registered as a second one: `rtc-reentrant.test.ts` pins
        // exactly one 'peer:connected' listener per init as the no-stacking invariant.
        voipStatsSampler.arm();
        // AVS-4485: a tab that (re)joins is present again; a hand-off bystander re-evaluates.
        const joinedSlot = remoteConnections?.[connectionId]?.meta?.clientIdHash;
        if (typeof joinedSlot === 'string' && joinedSlot) markVoipPeerPresent(joinedSlot);
        notifyVoipPeersChanged();
        // AVS-4302: don't announce this dealer's own tab connecting (a second same-dealer tab,
        // or a takeover reconnect) — that is "me", not a peer. A customer or a different dealer
        // is still announced.
        if (isOwnDealerPeer(remoteConnections?.[connectionId], user.userPseudonym, user.isDealer)) return;
        showToast({
          title: t`Your peer connected to the VOIP session`,
          type: 'success',
          notificationId: 'SUCCESS_VOIP_PEER_CONNECTED',
        });
      }
    )
  );

  voipHandlerUnsubscribes.push(
    userSession().once('session:end', () => {
      cleanupVoipHandlers();
      stopIceServersRefresh();
      voipStatsSampler.stop();
      voipRtcConnection?.destroy();
      broadcastingConnection?.disconnect();
    })
  );

  if (isDebugMode()) {
    // @ts-expect-error - expose config in global scope for debugging
    window.voipRtcConnection = voipRtcConnection;
  }

  return voipRtcConnection;
}
